import { useRef, useState } from 'react';
import type { Pilot } from '@edh/shared';
import { Avatar } from './Avatar';
import { RankedAvatar } from './RankedAvatar';
import { GuildMark } from './GuildMark';
import { claimSelf, registerSelf } from '../lib/api';
import { AvatarError, fileToAvatar } from '../lib/avatar';
import { useStore } from '../lib/store';

/**
 * Shown once, the first time an invited account signs in: pick the name and
 * face the rest of the guild will see you as.
 *
 * Nothing here is a credential. Google already established who you are; this is
 * only how you appear on the standings.
 */
export function Register({
  unclaimed,
  onDone,
}: {
  /** Members added by hand who nobody has signed in as yet. */
  unclaimed: Pilot[];
  onDone: () => void;
}) {
  const { state } = useStore();
  const guildName = state.settings.guildName;
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function pickImage(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setAvatar(await fileToAvatar(file));
    } catch (err) {
      setError(err instanceof AvatarError ? err.message : 'could not use that image');
    }
  }

  async function join() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await registerSelf({ name: name.trim(), avatar });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not join the guild');
      setBusy(false);
    }
  }

  async function claim(pilot: Pilot) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await claimSelf(pilot.id);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not claim that member');
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      {/* The league has loaded by now, so this is the live icon rather than a
          cached one — but showing it matters for the same reason: you should be
          able to see whose guild you are joining. */}
      <div className="signin-head">
        <GuildMark name={guildName} src={state.settings.guildIcon} size={56} />
        <div>
          <h1 style={{ fontSize: 24 }}>Join {guildName}</h1>
        </div>
      </div>
      <p className="sub" style={{ marginTop: -18 }}>
        Set up how the guild sees you.
      </p>

      {error && <div className="banner">{error}</div>}

      {/* Someone added by hand before they ever signed in already has a rating
          and a match history. Starting fresh beside a duplicate of themselves
          would strand both. */}
      {unclaimed.length > 0 && (
        <>
          <p className="hint" style={{ marginBottom: 10 }}>
            Already on the roster? Claim yourself and keep your rating.
          </p>
          <div className="list" style={{ marginBottom: 22 }}>
            {unclaimed.map((p) => (
              <button
                key={p.id}
                className="list-item"
                style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
                disabled={busy}
                onClick={() => void claim(p)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <RankedAvatar pilot={p} size={28} />
                  {p.name}
                </span>
                <span className="sub num">{p.placementGames}g</span>
              </button>
            ))}
          </div>
          <p className="hint" style={{ marginBottom: 14 }}>Or join as someone new:</p>
        </>
      )}

      <label className="field">
        <span>Name</span>
        <input
          className="input"
          value={name}
          placeholder="What the table calls you"
          maxLength={40}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void join();
          }}
        />
      </label>

      {/* Name first, so the initials in the preview below are already yours by
          the time you decide whether a photo is worth the trouble. */}
      <div className="field">
        <span>Picture (optional)</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={name || '?'} src={avatar} size={64} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              className="btn ghost"
              style={{ padding: '7px 14px', fontSize: 14 }}
              onClick={() => fileInput.current?.click()}
            >
              {avatar ? 'Change picture' : 'Add a picture'}
            </button>
            {avatar && (
              <button
                className="btn ghost"
                style={{ padding: '7px 14px', fontSize: 13, color: 'var(--muted)' }}
                onClick={() => setAvatar(null)}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void pickImage(e.target.files?.[0])}
        />
      </div>

      <button className="btn primary block" disabled={!name.trim() || busy} onClick={() => void join()}>
        {busy ? <span className="spinner" /> : `Join ${guildName}`}
      </button>
    </div>
  );
}
